// Site-wide link behavior for CF Span (loaded from layout/theme.liquid):
// 1. Links to videos (YouTube, Vimeo, video files) open in a lightbox instead of navigating.
// 2. Links to other sites open in a new tab.
(() => {
  // Hosts that count as this store, so their links stay in the same tab.
  const STORE_HOSTS = new Set([window.location.hostname, 'cfspan.com', 'www.cfspan.com', 'carbonbar.myshopify.com']);
  const VIDEO_FILE = /\.(mp4|webm|mov|m4v|ogv)$/i;

  const toUrl = (link) => {
    const href = link.getAttribute('href');
    if (!href || href.startsWith('#') || /^(mailto|tel|sms|javascript):/i.test(href)) return null;
    try {
      return new URL(href, window.location.href);
    } catch (error) {
      return null;
    }
  };

  // YouTube "t" / "start" values: "90", "90s", "1m30s", "1h2m3s"
  const toSeconds = (value) => {
    if (!value) return 0;
    if (/^\d+$/.test(value)) return Number(value);
    const [, h = 0, m = 0, s = 0] = value.match(/^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/) || [];
    return Number(h) * 3600 + Number(m) * 60 + Number(s);
  };

  // Returns { type: 'iframe' | 'file', src, portrait } for video links, otherwise null.
  const getVideo = (url) => {
    const host = url.hostname.replace(/^(www\.|m\.)/, '');

    if (host === 'youtube.com' || host === 'youtube-nocookie.com' || host === 'youtu.be') {
      let id = null;
      let portrait = false;
      if (host === 'youtu.be') {
        id = url.pathname.slice(1).split('/')[0];
      } else if (url.pathname === '/watch') {
        id = url.searchParams.get('v');
      } else {
        const match = url.pathname.match(/^\/(embed|shorts|live)\/([^/?#]+)/);
        if (match) {
          id = match[2];
          portrait = match[1] === 'shorts';
        }
      }
      if (!id) return null;

      const params = new URLSearchParams({ autoplay: '1', rel: '0', modestbranding: '1', playsinline: '1' });
      const start = toSeconds(url.searchParams.get('t') || url.searchParams.get('start'));
      if (start) params.set('start', start);
      if (url.searchParams.get('list')) params.set('list', url.searchParams.get('list'));
      return { type: 'iframe', src: `https://www.youtube-nocookie.com/embed/${id}?${params}`, portrait };
    }

    if (host === 'vimeo.com' || host === 'player.vimeo.com') {
      const match = url.pathname.match(/^\/(?:video\/)?(\d+)(?:\/([0-9a-f]+))?/);
      if (!match) return null;
      const params = new URLSearchParams({ autoplay: '1' });
      const hash = match[2] || url.searchParams.get('h');
      if (hash) params.set('h', hash);
      return { type: 'iframe', src: `https://player.vimeo.com/video/${match[1]}?${params}`, portrait: false };
    }

    if (VIDEO_FILE.test(url.pathname)) return { type: 'file', src: url.href, portrait: false };
    return null;
  };

  const isExternal = (url) => {
    if (!/^https?:$/.test(url.protocol) || STORE_HOSTS.has(url.hostname)) return false;
    // New customer accounts live on shopify.com but are part of the store experience.
    if (/(^|\.)shopify\.com$/.test(url.hostname) && /^\/\d+\/account/.test(url.pathname)) return false;
    return true;
  };

  // ---------- Video lightbox ----------

  let dialog = null;

  const getDialog = () => {
    if (dialog) return dialog;

    dialog = document.createElement('dialog');
    dialog.className = 'video-lightbox';
    dialog.setAttribute('aria-label', 'Video');
    dialog.innerHTML =
      '<div class="video-lightbox__frame"></div>' +
      '<button type="button" class="video-lightbox__close" aria-label="Close video">&times;</button>';
    document.body.append(dialog);

    dialog.querySelector('.video-lightbox__close').addEventListener('click', () => dialog.close());

    // Close when the dark backdrop (not the video) is clicked.
    let pressedOnBackdrop = false;
    dialog.addEventListener('pointerdown', (event) => {
      pressedOnBackdrop = event.target === dialog;
    });
    dialog.addEventListener('click', (event) => {
      if (pressedOnBackdrop && event.target === dialog) dialog.close();
    });

    // Remove the player on close so the video stops.
    dialog.addEventListener('close', () => {
      dialog.querySelector('.video-lightbox__frame').replaceChildren();
      document.documentElement.classList.remove('video-lightbox-open');
    });

    return dialog;
  };

  const openVideo = (video, title) => {
    const box = getDialog();
    const frame = box.querySelector('.video-lightbox__frame');
    let player;

    if (video.type === 'iframe') {
      player = document.createElement('iframe');
      player.src = video.src;
      player.allow = 'autoplay; fullscreen; picture-in-picture; encrypted-media';
      player.allowFullscreen = true;
      player.title = title || 'Video';
    } else {
      player = document.createElement('video');
      player.src = video.src;
      player.controls = true;
      player.autoplay = true;
      player.playsInline = true;
    }

    frame.classList.toggle('video-lightbox__frame--portrait', video.portrait);
    frame.classList.toggle('video-lightbox__frame--file', video.type === 'file');
    frame.replaceChildren(player);
    box.setAttribute('aria-label', title || 'Video');
    document.documentElement.classList.add('video-lightbox-open');
    box.showModal();
  };

  // ---------- External links ----------

  const markExternal = (link) => {
    if (link.target === '_blank' && link.dataset.newTab) return;
    link.target = '_blank';
    const rel = new Set((link.rel || '').split(/\s+/).filter(Boolean));
    rel.add('noopener');
    link.rel = [...rel].join(' ');
    if (!link.hasAttribute('aria-describedby') && document.getElementById('a11y-new-window-message')) {
      link.setAttribute('aria-describedby', 'a11y-new-window-message');
    }
    link.dataset.newTab = 'true';
  };

  const markAllExternal = () => {
    document.querySelectorAll('a[href]').forEach((link) => {
      const url = toUrl(link);
      if (url && isExternal(url) && !getVideo(url)) markExternal(link);
    });
  };

  // Bubble phase: sections that handle their own links (and call preventDefault) run first.
  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    const link = event.target.closest('a[href]');
    if (!link) return;

    const url = toUrl(link);
    if (!url) return;

    const video = getVideo(url);
    if (video && typeof HTMLDialogElement === 'function') {
      // Let people open the video page itself with a modifier key.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      openVideo(video, link.getAttribute('aria-label') || link.textContent.trim());
      return;
    }

    // Links added after page load (cart drawer, filters, search) get the new-tab treatment here.
    if (isExternal(url)) markExternal(link);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', markAllExternal);
  } else {
    markAllExternal();
  }
})();
